import { Children, forwardRef, isValidElement, useMemo, useRef } from 'react';
import cx from 'classnames';
import { useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { useDensity } from '../Density';
import { getListTokens } from './__internal__';
import { useListKeyDownHandler } from './useListKeyDownHandler';
import './List.css';
import cn from './List.css.json';

/**
 * The ListDesign component is a building block for creating other components. Specifically,
 * it is for building lists that don’t contain interactive elements. For example, it is
 * used by the Combo Box component’s drop-down list.
 *
 * This component lacks major features and accessibility support. Consumers who need to
 * create standalone lists or who need to create list items that contain interactive
 * elements such as buttons should use the List component instead.
 *
 * @param {ListDesignProps} props the ListDesignProps properties
 * @returns the ListDesign element
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const ListDesign = /*#__PURE__*/forwardRef(function ListDesign(props, ref) {
  const ulRef = useRef(null);
  const values = useMemo(() =>
  // Remove separators that were passed in to make keyboard nav easier
  Children.toArray(props.children).filter(element => /*#__PURE__*/isValidElement(element) && element.type && element.type?.name !== 'ListSeparator'), [props.children]);
  const handleKeyDown = useListKeyDownHandler({
    length: values.length,
    selectedIndex: props.selectedIndex,
    setFocusedIndex: props.setFocusedIndex,
    onSelectFocusedItem: props.onSelectFocusedItem,
    ulRef
  });
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokenStyle = useMemo(() => getListTokens(dt, density, props.theme, props.font, props.variant, props.whiteSpace), [dt, density, props.theme, props.font, props.variant, props.whiteSpace]);
  return /*#__PURE__*/_jsx("ul", {
    ref: ul => {
      ulRef.current = ul;
      setRef(ref, ul);
    },
    className: cx(props.className, !!props.theme && cn.themeOverride, cn.list),
    style: {
      ...tokenStyle,
      ...props.style
    },
    role: props.role ?? 'listbox',
    "aria-label": props.ariaLabel,
    tabIndex: props.focusable === false ? -1 : undefined,
    "aria-labelledby": Array.isArray(props.ariaLabelledBy) ? props.ariaLabelledBy.length ? props.ariaLabelledBy.join(' ') : undefined : props.ariaLabelledBy,
    "aria-describedby": Array.isArray(props.ariaDescribedBy) ? props.ariaDescribedBy.length ? props.ariaDescribedBy.join(' ') : undefined : props.ariaDescribedBy,
    "data-testid": props.testId,
    onKeyDown: handleKeyDown,
    id: props.id,
    children: props.children
  });
});
//# sourceMappingURL=ListDesign.js.map